import type { CaptureResult } from "@posthog/types";
import { isEuropeRegion } from "@/utils/common/region";

interface SuppressedExceptionRule {
  pattern: RegExp;
  reason: string;
}

/**
 * Exception patterns that should be silently dropped before reaching PostHog.
 * Each entry needs a regex that tests against $exception_message and a reason
 * explaining why the suppression is safe. To add a new rule, append an entry.
 */
export const SUPPRESSED_EXCEPTIONS: SuppressedExceptionRule[] = [
  {
    pattern: /Object captured as exception with keys:.*isCancelled/,
    // Metabase embed SDK throws a non-Error object { data, isCancelled, status }
    // when it cancels in-flight requests (e.g. during navigation or component
    // unmount). The reporting dashboard works correctly despite this; the object
    // is not a real error and is safe to suppress.
    reason: "Metabase embed SDK cancelled-request noise",
  },
  {
    pattern: /Object Not Found Matching Id:\d+, MethodName:update, ParamCount:\d+/,
    // Email link scanners (Microsoft Safe Links and similar) reject a promise
    // with this message before the app runs. Each event is its own session.
    reason: "Email link scanner promise rejection",
  },
];

const EUROPE_DROPPED_EVENTS = new Set([
  "$identify",
  "$set",
  "$groupidentify",
  "$create_alias",
]);

const SENSITIVE_PROPERTY_KEYS = new Set([
  "auth_event_id",
  "body",
  "company",
  "content",
  "conversation_id",
  "email",
  "error",
  "first_name",
  "firstname",
  "full_name",
  "fullname",
  "integration_id",
  "integration_name",
  "last_name",
  "lastname",
  "message",
  "message_id",
  "name",
  "org_name",
  "orgname",
  "path",
  "pathname",
  "phone",
  "phone_number",
  "phonenumber",
  "prompt",
  "query",
  "search_query",
  "stack",
  "stack_preview",
  "stackpreview",
  "text",
  "tool_id",
  "tool_name",
  "transcript",
  "url",
  "user_id",
  "website",
  "workflow_id",
  "$current_url",
  "$distinct_id",
  "$exception_message",
  "$exception_stack_trace_raw",
  "$pathname",
]);

function normalizePropertyKey(key: string): string {
  return key.replaceAll(/[$\s-]/g, "").toLowerCase();
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isSensitivePropertyKey(key: string): boolean {
  const normalizedKey = normalizePropertyKey(key);

  if (SENSITIVE_PROPERTY_KEYS.has(key) || SENSITIVE_PROPERTY_KEYS.has(normalizedKey)) {
    return true;
  }

  if (
    normalizedKey.includes("email") ||
    normalizedKey.includes("phone") ||
    normalizedKey.includes("website") ||
    normalizedKey.includes("url")
  ) {
    return true;
  }

  if (
    normalizedKey === "id" ||
    normalizedKey.endsWith("_id") ||
    normalizedKey.endsWith("id")
  ) {
    return true;
  }

  return normalizedKey.startsWith("error") || normalizedKey.startsWith("stack");
}

function sanitizeEuropeValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sanitizeEuropeValue);
  }

  if (isPlainObject(value)) {
    return sanitizeEuropeProperties(value);
  }

  return value;
}

export function sanitizeEuropeProperties(
  properties: Record<string, unknown>
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(properties)
      .filter(([key]) => !isSensitivePropertyKey(key))
      .map(([key, value]) => [key, sanitizeEuropeValue(value)])
  );
}

function shouldDropEuropeEvent(eventName: string): boolean {
  return (
    EUROPE_DROPPED_EVENTS.has(eventName) ||
    eventName === "$exception" ||
    eventName.startsWith("auth_event.")
  );
}

function exceptionMessages(
  properties: Record<string, unknown> | undefined
): string[] {
  if (!properties) {
    return [];
  }

  const messages: string[] = [];
  const singular = properties.$exception_message;
  if (typeof singular === "string" && singular.length > 0) {
    messages.push(singular);
  }

  // posthog-js stores the message on each exception. The singular
  // $exception_message property is missing on most ingested events.
  const list = properties.$exception_list;
  if (Array.isArray(list)) {
    for (const entry of list) {
      if (
        entry &&
        typeof entry === "object" &&
        "value" in entry &&
        typeof entry.value === "string" &&
        entry.value.length > 0
      ) {
        messages.push(entry.value);
      }
    }
  }

  return messages;
}

function frameLocation(frame: unknown): string | undefined {
  if (!isPlainObject(frame)) return undefined;
  if (typeof frame.filename === "string") return frame.filename;
  if (typeof frame.source === "string") return frame.source;
  return undefined;
}

/**
 * Google's reCAPTCHA script throws inside `recaptcha__en.js` with no gail-ui
 * frame. Match the script, not the message: `reading 'xS'` can also be app code.
 * Every exception in the event must have frames, and every frame must be that
 * script. A stackless sibling is an application error we still want.
 */
function isRecaptchaScriptCrash(
  properties: Record<string, unknown> | undefined
): boolean {
  const list = properties?.$exception_list;
  if (!Array.isArray(list) || list.length === 0) return false;

  const frames: unknown[] = [];
  for (const entry of list) {
    if (!isPlainObject(entry) || !isPlainObject(entry.stacktrace)) return false;
    if (!Array.isArray(entry.stacktrace.frames) || entry.stacktrace.frames.length === 0) {
      return false;
    }
    frames.push(...entry.stacktrace.frames);
  }

  return frames.every((frame) => frameLocation(frame)?.includes("recaptcha__en.js") === true);
}

function filterSuppressedException(
  event: CaptureResult | null
): CaptureResult | null {
  if (event?.event !== "$exception") {
    return event;
  }

  const properties = event.properties as Record<string, unknown> | undefined;
  const messages = exceptionMessages(properties);

  if (
    messages.some((message) =>
      SUPPRESSED_EXCEPTIONS.some((rule) => rule.pattern.test(message))
    )
  ) {
    return null;
  }

  if (isRecaptchaScriptCrash(properties)) {
    return null;
  }

  return event;
}

export function filterPostHogEventForRegion(
  event: CaptureResult | null,
  region: string | undefined = process.env.NEXT_PUBLIC_REGION
): CaptureResult | null {
  if (!isEuropeRegion(region)) {
    return filterSuppressedException(event);
  }

  if (!event) {
    return event;
  }

  if (shouldDropEuropeEvent(event.event)) {
    return null;
  }

  if (!isPlainObject(event.properties)) {
    return event;
  }

  return {
    ...event,
    properties: sanitizeEuropeProperties(event.properties),
  };
}

/**
 * PostHog `before_send` callback. Global deployments only suppress known noisy
 * exceptions. Europe deployments drop identity/error events and remove
 * PII-bearing properties before events leave the browser.
 */
export function filterPostHogEvent(
  event: CaptureResult | null
): CaptureResult | null {
  return filterPostHogEventForRegion(event);
}
